<script setup lang="ts">
import { PrivacySettingsSymbol, ProjectGrantSymbol, UserGrantSymbol, UserManagerSymbol, UserSymbol } from '@d/auth/helper/auth'
import { ProjectGrant } from '@d/auth/helper/permission/enum'
import { manualPublicApi } from '@d/sdk'
import { mdiAccount, mdiFileDocument, mdiFileLock, mdiLogout, mdiMoonWaxingCrescent, mdiShield, mdiWhiteBalanceSunny, mdiCloudPrint, mdiNewspaperVariantMultiple, mdiViewDashboard, mdiKeyboard } from '@mdi/js'
import { useTheme } from 'vuetify'
import { useDeviceBreakpoint } from '../../composables/useDeviceBreakpoint'
import { isNewsTenant } from '../../helper/news'
import { useStateScope } from '../../helper/shortcut'
import Breadcrumbs from '../file/Breadcrumbs.vue'
import Shortcut from '../util/Shortcut.vue'

interface Props {
  showToggleDrawer?: boolean
  leftDrawerSize?: number
  title?: string
}

interface Items {
  title: string
  icon: {
    icon: string
    style?: string
  }
  action?: () => void
  href?: string
  style?: string
  condition?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showToggleDrawer: false,
})
const emit = defineEmits(['toggleDrawer'])

const route = useRoute('client-view-path')
// const router = useRouter()

const isManual = computed(() => ['manual-shared-tenant-key-doc', 'manual-shared-tenant-key-print', 'manual-shared-tenant-key-index'].includes(route.name))
const isNews = computed(() => isNewsTenant())

const isDark = useDark()
const darkToggle = useToggle(isDark)
const usergrants = inject(UserGrantSymbol, null)
const user = inject(UserSymbol, null)
const usermanager = inject(UserManagerSymbol, null)
const theme = useTheme()
const privacySettings = inject(PrivacySettingsSymbol, null)

const windowSize = useWindowSize()
const leftDrawerSizePx = computed(() => (windowSize.width.value * (props.leftDrawerSize ?? 0) / 100))

const deviceBreakpoint = useDeviceBreakpoint()

const tenant = computed(() => user?.value?.profile.aud[user?.value.profile.aud.length - 2])

const projectGrants = inject(ProjectGrantSymbol, null)

const project = computed(() => {
  return usergrants?.value?.find(grant => grant.projectId === tenant.value)
})

const { t } = useI18n({
  useScope: 'local',
})

function toggleDrawer(): void {
  emit('toggleDrawer')
}

const showSwitch = computed(() => {
  const themes = theme.themes.value
  if (themes.lightZitadel === undefined && themes.darkZitadel === undefined)
    return true
  if (themes.lightZitadel !== undefined && themes.darkZitadel !== undefined)
    return true
  return false
})

const items = computed<Items[]>(() => [
  {
    title: t('administration'),
    icon: {
      icon: mdiShield,
    },
    action: () => goToAdministration(),
    condition: projectGrants?.value?.includes(ProjectGrant.manage),
  },
  {
    title: t('profile'),
    action: () => goToMe(),
    icon: {
      icon: mdiAccount,
    },
    condition: true,
  },
  // {
  //   title: t('profile'),
  //   href: `${config.authority}/ui/console/users/me`,
  //   icon: {
  //     icon: mdiAccount,
  //   },
  // },
  {
    title: t('logout'),
    action: async () => {
      try {
        if (usermanager?.value !== undefined) {
          await usermanager.value.signoutRedirect()
        }
        else {
          await navigateTo({ name: 'logout' })
        }
      }
      catch (e) {
        console.error('signoutRedirect', e)
        await navigateTo({ name: 'logout' })
      }
    },
    icon: {
      icon: mdiLogout,
    },
    condition: true,
  },
  {
    title: t(`dark${theme.current.value.dark}`),
    icon: {
      icon: theme.current.value.dark ? mdiMoonWaxingCrescent : mdiWhiteBalanceSunny,
      style: theme.current.value.dark ? 'padding-right: 5px' : '',
    },
    action: () => darkToggle(),
    condition: showSwitch.value,
  },
  {
    title: t('documentation'),
    icon: {
      icon: mdiFileDocument,
    },
    href: 'https://doc.scale-it.cloud/manual/shared/293297941743945238/say34cb37343aknoh6fpds4uc13n81x7',
    condition: true,
  },
  {
    title: t('changelog'),
    icon: {
      icon: mdiNewspaperVariantMultiple,
    },
    href: 'https://doc.scale-it.cloud/manual/shared/293297941743945238/tjql45q0z81432gjokylo4lun1x7uk9c',
    condition: true,
  },
  {
    title: t('termsOfService'),
    icon: {
      icon: mdiFileDocument,
    },
    style: 'opacity: 0.5',
    href: privacySettings?.value?.tosLink,
    condition: true,
  },
  {
    title: t('privacyPolicy'),
    icon: {
      icon: mdiFileLock,
    },
    style: 'opacity: 0.5',
    href: privacySettings?.value?.privacyLink,
    condition: true,
  },
])

const tablet = computed(() => deviceBreakpoint.smaller('laptop').value)
const slots = useSlots()

function goToAdministration(): void {
  navigateTo({ name: 'client-administration', params: { client: route.params.client } })
}

function goToMe(): void {
  navigateTo({ name: 'client-me', params: { client: route.params.client } })
}

const manualRoute = useRoute('manual-shared-tenant-key')

function toPrintView() {
  const params = (route.params as unknown) as { key: string }
  navigateTo({ name: 'manual-shared-tenant-key-print', params: { tenant: manualRoute.params.tenant, key: params.key } })
}

const image = computed(() => {
  return 'https://brand.uponu.com/external/web/logo/standard/Logo_Standard_Anthracite.svg'
})

const showLogo = computedAsync(async () => {
  const theme = await manualPublicApi.getThemeSettings(manualRoute.params.tenant, manualRoute.params.key)
  return theme.useThemeLogo && !theme.useStandardTheme
})

const showKeybinds = useLocalStorage('showKeybinds', true)

function switchShowKeybinds() {
  showKeybinds.value = !showKeybinds.value
}

function navigateToDashboard() {
  window.location.href = 'https://app.trustgate.cloud/'
}

const shortcutScope = useStateScope()
</script>

<template>
  <v-app-bar color="primary" prominent elevation="0">
    <Shortcut keys="F1" :action="switchShowKeybinds" hidden prevent-default />
    <div class="app-bar-content">
      <div :style="{ 'min-width': leftDrawerSizePx > 0 && !tablet ? `${leftDrawerSizePx ?? 0}px` : undefined }">
        <div style="position: relative">
          <v-app-bar-nav-icon v-if="showToggleDrawer" variant="text" @click.stop="toggleDrawer" />
          <Shortcut v-if="showToggleDrawer" prevent-default keys="alt+t" :action="toggleDrawer" floating top="35px" color="white" />
        </div>
        <div v-if="!isManual" style="position: relative">
          <v-tooltip location="bottom">
            <template #activator="{ props: props2 }">
              <v-btn v-bind="props2" href="https://app.trustgate.cloud/" class="mr-2" icon :disabled="shortcutScope?.isLoadingBlocking.value ?? false">
                <v-icon :icon="mdiViewDashboard" />
              </v-btn>
            </template>
            Dashboard
          </v-tooltip>
          <Shortcut keys="alt+q" prevent-default :action="navigateToDashboard" floating top="35px" color="white" :disabled="shortcutScope?.isLoadingBlocking.value ?? false" />
        </div>
        <slot name="left" />
        <v-toolbar-title v-if="title" class="title">
          {{ title }}
        </v-toolbar-title>
        <v-toolbar-title v-else-if="project && !tablet" class="title">
          {{ project.orgName }} - {{ project?.projectName }}
        </v-toolbar-title>
        <v-toolbar-title v-else-if="!tablet">
          <span style="padding-left: 12px;">{{ t('serviceName') }}</span>
        </v-toolbar-title>
      </div>
      <v-divider v-if="!tablet" vertical />
      <div v-if="!isManual" style="flex:1">
        <Breadcrumbs />
      </div>
      <div v-else-if="isManual && showLogo" style="flex:1; display: flex; justify-content: center;">
        <div style="flex: 0;">
          <img :src="image" style="height: 25px;margin-top: 6px">
        </div>
      </div>

      <div style="flex:0">
        <v-menu v-if="user && user.profile">
          <template #activator="{ props: props2 }">
            <!-- <v-avatar color="background" class="mr-2" v-bind="props">
              {{ userAbbreviation }}
            </v-avatar> -->
            <v-btn
              class="text-none mr-2" v-bind="props2" size="default"
              color="on-primary" variant="text"
            >
              <v-icon v-if="isNews" :icon="mdiNewspaperVariantMultiple" class="mr-1" />
              <v-icon :icon="mdiAccount" class="mr-1" />
              <span v-if="!tablet">{{ user.profile.name }}</span>
            </v-btn>
          </template>

          <v-list>
            <template v-for="(item, index) in items">
              <v-list-item
                v-if="item.condition" :key="index" :value="index" :href="item.href"
                :style="item.style" target="_blank" @click="item.action"
              >
                <v-list-item-title>{{ item.title }}</v-list-item-title>
                <template #prepend>
                  <v-icon :icon="item.icon.icon" :style="item.icon.style" />
                </template>
              </v-list-item>
            </template>
            <v-divider />
            <v-list-item @click="switchShowKeybinds">
              <v-list-item-title> {{ t('switchShortcutHelp') }} <v-hotkey keys="F1" /></v-list-item-title>
              <template #prepend>
                <v-icon :icon="mdiKeyboard" />
              </template>
            </v-list-item>
          </v-list>
        </v-menu>
        <div v-if="isManual">
          <v-btn class="mr-2" :icon="mdiCloudPrint" @click="toPrintView" />
        </div>
      </div>
    </div>

    <template v-if="slots.tabs" #extension>
      <slot name="tabs" />
    </template>
  </v-app-bar>
</template>

<style scoped>
.app-bar-content {
  width: 100%;
  display: flex;
  align-items: center;

  >div:first-child {
    display: flex;
    align-items: center;
    padding-right: 10px;
  }
}
</style>

<i18n lang="yaml">
de:
  administration: Administration
  profile: Mein Profil
  logout: Ausloggen
  darktrue: Dunkel
  darkfalse: Hell
  serviceName: knowing:u
  deleteTenant: Projekt löschen
  termsOfService: Impressum
  privacyPolicy: Datenschutz
  documentation: Dokumentation
  changelog: Changelog
  switchShortcutHelp: Hilfe zu Tastenkombinationen
en:
  administration: Administration
  profile: My Profile
  logout: Logout
  darktrue: Dark
  darkfalse: Light
  serviceName: knowing:u
  deleteTenant: Projekt löschen
  termsOfService: Terms of Service
  privacyPolicy: Privacy Policy
  documentation: Documentation
  changelog: Changelog
  switchShortcutHelp: Shortcut help
</i18n>
